import type { ReactNode } from 'react';

//  Form field shell

interface DmFieldProps {
    label: string;
    hint?: string;
    required?: boolean;
    error?: string;
    children: ReactNode;
    className?: string;
}

/**
 * Standard field wrapper for DM action landings: uppercase muted label with
 * optional required-marker, the input itself, and a hint or error line
 * underneath. Designed to drop in alongside `dmInputClasses` /
 * `dmInputErrorClasses` so every form looks the same.
 */
function DmField({ label, hint, required, error, children, className }: DmFieldProps) {
    return (
        <div className={`flex flex-col gap-1.5 ${className ?? ''}`}>
            <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                {label}
                {required && <span className="text-red-400 ml-0.5">*</span>}
            </label>
            {children}
            {error ? (
                <p className="text-xs text-red-600">{error}</p>
            ) : hint ? (
                <p className="text-xs text-gray-400">{hint}</p>
            ) : null}
        </div>
    );
}

//  Input classes

const dmInputClasses =
    'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder:text-gray-300 transition';

const dmInputErrorClasses =
    'w-full border border-red-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent placeholder:text-gray-300 transition';

//  Date input
// Plain <input type="date"> styled to match dmInputClasses. Value/onChange use
// the native YYYY-MM-DD string (what the endpoint expects); '' means "clear".

interface DmDateInputProps {
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
}

function DmDateInput({ value, onChange, error }: DmDateInputProps) {
    return (
        <input
            type="date"
            className={error ? dmInputErrorClasses : dmInputClasses}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

//  Textarea

interface DmTextareaProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    error?: boolean;
    rows?: number;
}

function DmTextarea({ value, onChange, placeholder, error, rows = 3 }: DmTextareaProps) {
    return (
        <textarea
            className={error ? dmInputErrorClasses : dmInputClasses}
            placeholder={placeholder}
            value={value}
            rows={rows}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

//  Select input

interface DmSelectOption {
    value: string;
    label: string;
}

interface DmSelectProps {
    value: string;
    onChange: (value: string) => void;
    options: DmSelectOption[];
    error?: boolean;
}

function DmSelect({ value, onChange, options, error }: DmSelectProps) {
    return (
        <select
            className={error ? dmInputErrorClasses : dmInputClasses}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

//  Button primitives

interface ButtonProps {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    type?: 'button' | 'submit';
}

type DmBtnSize = 'md' | 'sm';

const DM_BTN_PADDING: Record<DmBtnSize, string> = {
    md: 'px-4 py-2',
    sm: 'px-3 py-1.5'
};

function DmBtnPrimary({
    children,
    onClick,
    disabled,
    type = 'button',
    size = 'md'
}: ButtonProps & { size?: DmBtnSize }) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`${DM_BTN_PADDING[size]} bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
        >
            {children}
        </button>
    );
}

function DmBtnSecondary({ children, onClick, disabled, type = 'button' }: ButtonProps) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className="px-4 py-2 bg-white text-gray-700 text-sm font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
        >
            {children}
        </button>
    );
}

function DmBtnDestructive({ children, onClick, disabled, type = 'button' }: ButtonProps) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
            {children}
        </button>
    );
}

export {
    DmField,
    dmInputClasses,
    dmInputErrorClasses,
    DmDateInput,
    DmSelect,
    DmTextarea,
    DmBtnPrimary,
    DmBtnSecondary,
    DmBtnDestructive
};
